import axios from "axios";
import { createCloudRunAxios } from "./axios";
import { logger } from "../utils/logger";

const autocompleteApi = createCloudRunAxios("searchAutocomplete");

// Types for the autocomplete service
export interface AutocompleteSuggestion {
  type:
    | "address"
    | "city"
    | "zip"
    | "neighborhood"
    | "county"
    | "natural_language"
    | "listingid";
  display: string;
  value: string;
  metadata: {
    property_count?: number;
    city?: string;
    state?: string;
    zip?: string;
    neighborhood?: string;
    county?: string;
    street_number?: string;
    street_name?: string;
    street_suffix?: string;
    first_image?: string;
    listingkey?: string;
    unparsedaddress?: string;
    listingid?: string;
    originatingsystemname?: string;
  };
}

export interface AutocompleteResponse {
  suggestions: AutocompleteSuggestion[];
  query: string;
  count: number;
}

export interface AutocompleteRequest {
  query: string;
  tenant_id: string; // Required tenant ID
  limit?: number;
  types?: Array<
    | "address"
    | "city"
    | "zip"
    | "neighborhood"
    | "county"
    | "natural_language"
    | "listingid"
  >;
}

// Errors propagate to the caller. React Query owns retry/error state, and the
// autocomplete hook degrades to an empty suggestion list (`data?.suggestions ||
// []`), so a failed fetch shows an empty dropdown rather than an error banner —
// the right UX for a typeahead — without swallowing the failure at this layer.
export const locationAutocompleteService = {
  async getAutocompleteResults(
    request: AutocompleteRequest,
    signal?: AbortSignal
  ): Promise<AutocompleteResponse> {
    try {
      const response = await autocompleteApi.post<AutocompleteResponse>(
        "",
        {
          query: request.query,
          tenant_id: request.tenant_id,
          limit: request.limit || 10,
          types: request.types,
        },
        { signal }
      );

      return response.data;
    } catch (error) {
      // A stale keystroke aborted by React Query is not a real failure.
      if (axios.isCancel(error)) {
        throw error;
      }
      logger.error("Error fetching autocomplete suggestions", { error });
      throw error;
    }
  },
};
